/**
 * Лёгкий клиентский кэш GET-запросов: last-known сразу на экране,
 * один inflight на ключ, stale-while-revalidate при повторном заходе.
 *
 * Не тащим react-query: вкладки владельца читают одни и те же read-модели
 * (/attention, /dashboard, деньги, брони) из разных экранов и бейджей.
 */

const SESSION_PREFIX = 'jack.swr.v1:';
const MAX_SESSION_CHARS = 240_000;
export const CACHE_FRESH_MS = 8_000;
const FRESH_MS = CACHE_FRESH_MS;

type Entry<T> = {
  data?: T;
  updatedAt: number;
  inflight?: Promise<T>;
  generation: number;
};

const memory = new Map<string, Entry<unknown>>();
const scopeListeners = new Set<() => void>();
let scope = 'anonymous';
let generation = 0;

/** Memory and session entries belong to one signed-in account. A late response from the previous account cannot write them back. */
export function setQueryCacheScope(next: string): void {
  if (next === scope) return;
  scope = next;
  generation += 1;
  memory.clear();
  clearSessionStore();
  for (const listener of scopeListeners) listener();
}

export function onQueryCacheScopeChange(listener: () => void): () => void {
  scopeListeners.add(listener);
  return () => scopeListeners.delete(listener);
}

export function cacheGeneration(): number {
  return generation;
}

function scopedKey(key: string): string {
  return `${scope}::${key}`;
}

function readSession<T>(key: string): T | undefined {
  if (typeof sessionStorage === 'undefined') return undefined;
  try {
    const raw = sessionStorage.getItem(SESSION_PREFIX + key);
    if (!raw) return undefined;
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function writeSession<T>(key: string, data: T): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const raw = JSON.stringify(data);
    if (raw.length > MAX_SESSION_CHARS) return;
    sessionStorage.setItem(SESSION_PREFIX + key, raw);
  } catch {
    /* Quota or private mode — memory cache still works. */
  }
}

function clearSessionStore(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const remove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const name = sessionStorage.key(i);
      if (name?.startsWith(SESSION_PREFIX)) remove.push(name);
    }
    for (const name of remove) sessionStorage.removeItem(name);
  } catch {
    /* ignore */
  }
}

function hydrate<T>(key: string): T | undefined {
  const storedKey = scopedKey(key);
  const mem = memory.get(storedKey) as Entry<T> | undefined;
  if (mem?.data !== undefined) return mem.data;
  const persisted = readSession<T>(storedKey);
  if (persisted === undefined) return undefined;
  memory.set(storedKey, { data: persisted, updatedAt: 0, generation });
  return persisted;
}

export function peekCache<T>(key: string): T | undefined {
  return hydrate<T>(key);
}

/** True only for a memory entry written in this account scope inside the freshness window. */
export function cacheIsFresh(key: string, now = Date.now()): boolean {
  const entry = memory.get(scopedKey(key));
  if (!entry || entry.data === undefined || entry.generation !== generation) return false;
  return now - entry.updatedAt < FRESH_MS;
}

export function writeCache<T>(key: string, data: T, persist = true, atGeneration = generation): void {
  if (atGeneration !== generation) return;
  const storedKey = scopedKey(key);
  memory.set(storedKey, {
    data,
    updatedAt: Date.now(),
    generation,
    inflight: (memory.get(storedKey) as Entry<T> | undefined)?.inflight,
  });
  if (persist) writeSession(storedKey, data);
}

export function invalidateCache(key: string): void {
  const entry = memory.get(scopedKey(key));
  if (entry) {
    entry.updatedAt = 0;
    entry.inflight = undefined;
  }
}

export function clearQueryCache(): void {
  scope = 'anonymous';
  generation += 1;
  memory.clear();
  clearSessionStore();
  for (const listener of scopeListeners) listener();
}

export function cachedGet<T>(
  key: string,
  fetcher: () => Promise<T>,
  options?: { persist?: boolean; force?: boolean },
): Promise<T> {
  const persist = options?.persist ?? true;
  const started = generation;
  const storedKey = scopedKey(key);
  const existing = memory.get(storedKey) as Entry<T> | undefined;
  if (existing?.inflight && existing.generation === started) return existing.inflight;
  if (
    !options?.force
    && existing?.data !== undefined
    && existing.generation === started
    && Date.now() - existing.updatedAt < FRESH_MS
  ) {
    return Promise.resolve(existing.data);
  }

  const entry: Entry<T> = {
    data: existing?.generation === started ? existing.data : hydrate<T>(key),
    updatedAt: existing?.generation === started ? existing.updatedAt : 0,
    generation: started,
  };
  const inflight = fetcher()
    .then((data) => {
      if (started !== generation || memory.get(storedKey) !== entry) return data;
      entry.inflight = undefined;
      writeCache(key, data, persist, started);
      return data;
    })
    .catch((error: unknown) => {
      if (started === generation && memory.get(storedKey) === entry) entry.inflight = undefined;
      throw error;
    });
  entry.inflight = inflight;
  memory.set(storedKey, entry);
  return inflight;
}
